// ── TrainingPricing.jsx ───────────────────────────────────────────────────────
// FIXES:
//  - tp-pricing-card::before (border animation) gets will-change: background-position
//  - pricing-card hover uses will-change: transform
//  - pricing-actions discord row wraps on narrow screens
//  - pricing-card padding reduced gracefully on 480px
import { Eyebrow, DiscordIcon, ChevronRight } from "./TrainingShared";
import { PRICING_FEATURES } from "./data/trainingData";

export const PRICING_CSS = `
  .tp-pricing-wrap { margin-top: 4rem; }
  .tp-pricing-card {
    max-width: 600px;
    margin: 0 auto;
    background: var(--tp-surface);
    border: 1px solid rgba(77,127,255,.22);
    border-radius: 28px;
    padding: 3.5rem 3rem;
    position: relative;
    overflow: hidden;
    text-align: center;
    box-shadow:
      0 48px 96px rgba(0,0,0,.42),
      0 0 60px rgba(77,127,255,.065),
      0 0 0 1px rgba(77,127,255,.06) inset;
    transition: transform .28s cubic-bezier(.16,1,.3,1), box-shadow .28s;
    will-change: transform;
  }
  .tp-pricing-card:hover {
    transform: translateY(-6px);
    box-shadow:
      0 60px 110px rgba(0,0,0,.48),
      0 0 90px rgba(77,127,255,.1),
      0 0 0 1px rgba(77,127,255,.1) inset;
  }
  .tp-pricing-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--tp-blue), var(--tp-violet), var(--tp-pink), var(--tp-amber), var(--tp-blue));
    background-size: 300% 100%;
    animation: tp-border-rotate 4s linear infinite;
    will-change: background-position;
  }
  .tp-pricing-shimmer {
    position: absolute;
    top: -50%; left: -50%;
    width: 200%; height: 200%;
    background: radial-gradient(circle at 30% 30%, rgba(77,127,255,.055), transparent 50%);
    pointer-events: none;
  }
  .tp-pricing-amount {
    font-size: clamp(4rem, 12vw, 6.5rem);
    font-weight: 600;
    color: var(--tp-gold);
    line-height: 1;
    text-shadow: 0 0 60px rgba(245,158,11,.32), 0 0 120px rgba(245,158,11,.12);
    position: relative;
    letter-spacing: -.04em;
  }
  .tp-pricing-sub {
    font-size: .72rem;
    color: var(--tp-t3);
    letter-spacing: 3px;
    text-transform: uppercase;
    font-family: var(--tp-mono);
    margin-top: .75rem;
    margin-bottom: 2.5rem;
  }
  .tp-pricing-features {
    display: flex;
    flex-direction: column;
    gap: .875rem;
    margin: 0 auto 2.5rem;
    max-width: 420px;
    text-align: left;
  }
  .tp-pricing-feat {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: .95rem;
    color: var(--tp-t2);
    transition: color .18s;
  }
  .tp-pricing-feat:hover { color: var(--tp-t1); }
  .tp-feat-check {
    width: 22px; height: 22px;
    border-radius: 50%;
    background: rgba(15,214,138,.08);
    border: 1px solid rgba(15,214,138,.28);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--tp-green);
    font-size: .7rem;
    font-weight: 700;
    transition: background .18s, box-shadow .18s;
  }
  .tp-pricing-feat:hover .tp-feat-check {
    background: rgba(15,214,138,.14);
    box-shadow: 0 0 10px rgba(15,214,138,.2);
  }

  .tp-pricing-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: .875rem;
  }
  .tp-pricing-discord-row {
    display: flex;
    align-items: center;
    gap: .5rem;
    font-size: .75rem;
    color: var(--tp-t3);
    flex-wrap: wrap;
    justify-content: center;
  }
  .tp-pricing-discord-sep {
    color: var(--tp-line2);
    font-size: .7rem;
  }

  .tp-pricing-enroll-wrap {
    position: relative;
    display: inline-flex;
  }
  .tp-pricing-enroll-ring {
    position: absolute;
    inset: -5px;
    border-radius: 19px;
    border: 1px solid rgba(77,127,255,.28);
    animation: tp-glow-ring 2.6s ease-in-out infinite;
    pointer-events: none;
    will-change: box-shadow;
  }

  @media (max-width: 640px) {
    .tp-pricing-card { padding: 2.5rem 1.5rem; border-radius: 22px; }
    .tp-pricing-amount { font-size: clamp(3rem, 14vw, 5rem); }
    .tp-pricing-enroll-wrap { width: 100%; }
    .tp-pricing-enroll-wrap .tp-btn-primary { width: 100%; justify-content: center; }
  }
  @media (max-width: 480px) {
    .tp-pricing-card { padding: 2rem 1.25rem; }
  }
`;

export default function TrainingPricing({ scrollToPayment }) {
  return (
    <section className="tp-section" style={{ textAlign: "center" }}>
      <div className="tp-container">
        <Eyebrow center>Pricing</Eyebrow>
        <h2 className="tp-section-h2">
          One Simple <em>Investment.</em>
        </h2>
        <p className="tp-section-sub" style={{ margin: "0 auto" }}>
          No hidden fees. No upsells. ₨2,000 covers your full training month —
          the 3-month internship is completely free.
        </p>
        <div className="tp-pricing-wrap">
          <div className="tp-pricing-card">
            <div className="tp-pricing-shimmer" />
            <div className="tp-pricing-amount">₨2,000</div>
            <div className="tp-pricing-sub">One-time fee · No hidden charges</div>
            <div className="tp-pricing-features">
              {PRICING_FEATURES.map((item) => (
                <div key={item} className="tp-pricing-feat">
                  <span className="tp-feat-check">✓</span>
                  {item}
                </div>
              ))}
            </div>

            <div className="tp-pricing-actions">
              <div className="tp-pricing-enroll-wrap">
                <div className="tp-pricing-enroll-ring" aria-hidden="true" />
                <button
                  className="tp-btn-primary"
                  onClick={scrollToPayment}
                  style={{ fontSize: "1rem", padding: "1rem 2.75rem" }}
                >
                  Enroll for ₨2,000
                  <ChevronRight />
                </button>
              </div>

              <div className="tp-pricing-discord-row">
                <a
                  href="https://discord.gg/qCvxh7ZhSv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tp-btn-discord"
                  style={{ padding: ".6rem 1.25rem", fontSize: ".78rem" }}
                >
                  <DiscordIcon size={14} />
                  Join Community
                </a>
                <span className="tp-pricing-discord-sep">·</span>
                <span style={{ fontSize: ".73rem", color: "var(--tp-t3)" }}>
                  Free, no enrollment needed
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
